import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, MapPin, Star, Filter, Heart, X, Music, Shield, CheckCircle, Disc3, Users, Navigation, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';

import { API_URL } from '../utils/api';

export default function ClubsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedSkill, setSelectedSkill] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Distance filter state
  const [locationInput, setLocationInput] = useState('');
  const [distance, setDistance] = useState(searchParams.get('distance') || 'nationwide');
  const [userCoords, setUserCoords] = useState(
    searchParams.get('lat') && searchParams.get('lng') 
      ? { lat: parseFloat(searchParams.get('lat')), lng: parseFloat(searchParams.get('lng')) }
      : null
  );
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    fetchClubs();
  }, [searchParams]);

  // Initialize location input if coming from homepage with coords
  useEffect(() => {
    if (searchParams.get('lat') && searchParams.get('lng')) {
      setLocationInput('Using your location');
    }
  }, []);

  const fetchClubs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const search = searchParams.get('search');
      if (search) params.append('search', search);
      if (selectedLocation) params.append('location', selectedLocation);
      if (selectedSkill) params.append('skill_level', selectedSkill);
      
      // Distance filtering
      const lat = searchParams.get('lat');
      const lng = searchParams.get('lng');
      const dist = searchParams.get('distance');
      if (lat && lng && dist && dist !== 'nationwide') {
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('distance', dist);
      }

      const response = await fetch(`${API_URL}/api/clubs?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setClubs(data);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get user's location via browser geolocation
  const getUserLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationInput('Using your location');
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please enter a postcode or town.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Geocode a postcode/location
  const geocodeLocation = async (location) => {
    try {
      const response = await fetch(`${API_URL}/api/geocode?address=${encodeURIComponent(location)}`);
      if (response.ok) {
        const data = await response.json();
        return { lat: data.latitude, lng: data.longitude };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  };

  const handleLocationInputChange = (e) => {
    setLocationInput(e.target.value);
    if (userCoords && e.target.value !== 'Using your location') {
      setUserCoords(null);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    
    // Handle distance filtering
    if (distance !== 'nationwide') {
      let coords = userCoords;
      
      if (locationInput && locationInput !== 'Using your location' && !userCoords) {
        coords = await geocodeLocation(locationInput);
        if (coords) setUserCoords(coords);
      }
      
      if (coords) {
        params.set('lat', coords.lat);
        params.set('lng', coords.lng);
        params.set('distance', distance);
      }
    }
    
    setSearchParams(params);
  };

  const applyFilters = () => {
    const params = new URLSearchParams(searchParams);
    if (selectedLocation) params.set('location', selectedLocation);
    else params.delete('location');
    if (selectedSkill) params.set('skill', selectedSkill);
    else params.delete('skill');
    setSearchParams(params);
    setShowFilters(false);
    fetchClubs();
  };

  const clearFilters = () => {
    setSelectedLocation('');
    setSelectedSkill('');
    setSearchQuery('');
    setLocationInput('');
    setDistance('nationwide');
    setUserCoords(null);
    setSearchParams({});
    fetchClubs();
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        size={14}
        className={i < Math.floor(rating) ? 'fill-violet-500 text-violet-500' : 'text-slate-300'}
      />
    ));
  };

  const ukRegions = ['Bedfordshire', 'Berkshire', 'Bristol', 'Buckinghamshire', 'Cambridgeshire', 'Cheshire', 'Cornwall', 'Cumbria', 'Derbyshire', 'Devon', 'Dorset', 'Durham', 'East Sussex', 'Essex', 'Gloucestershire', 'Greater London', 'Greater Manchester', 'Hampshire', 'Herefordshire', 'Hertfordshire', 'Isle of Wight', 'Kent', 'Lancashire', 'Leicestershire', 'Lincolnshire', 'Merseyside', 'Norfolk', 'North Yorkshire', 'Northamptonshire', 'Northumberland', 'Nottinghamshire', 'Oxfordshire', 'Rutland', 'Shropshire', 'Somerset', 'South Yorkshire', 'Staffordshire', 'Suffolk', 'Surrey', 'Tyne and Wear', 'Warwickshire', 'West Midlands', 'West Sussex', 'West Yorkshire', 'Wiltshire', 'Worcestershire', 'Scotland', 'Wales', 'Northern Ireland'];
  const skillLevels = ['Beginner', 'Improver', 'Intermediate+'];

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="clubs-page">
      {/* Page header (shared) */}
      <PageHeader
        title="Line Dance Clubs"
        subtitle={`${clubs.length} club${clubs.length !== 1 ? 's' : ''} found`}
      />
      {/* Search toolbar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 lg:px-12 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-end gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <form onSubmit={handleSearch} className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search clubs..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11 rounded-lg border-slate-200"
                    data-testid="clubs-search-input"
                  />
                </div>
                
                {/* Location Input with Geolocation */}
                <div className="relative flex-1 min-w-[180px]">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Postcode or town..."
                    value={locationInput}
                    onChange={handleLocationInputChange}
                    className="pl-10 pr-10 h-11 rounded-lg border-slate-200"
                    data-testid="clubs-location-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={getUserLocation}
                    disabled={gettingLocation}
                    aria-label="Use my location"
                    title="Use my location"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 text-violet-600 hover:text-violet-600 hover:bg-violet-50"
                  >
                    {gettingLocation ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Navigation className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {/* Distance Select */}
                <Select value={distance} onValueChange={setDistance}>
                  <SelectTrigger className="w-36 h-11 border-slate-200" data-testid="clubs-distance-select">
                    <SelectValue placeholder="Distance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Within 5 miles</SelectItem>
                    <SelectItem value="10">Within 10 miles</SelectItem>
                    <SelectItem value="25">Within 25 miles</SelectItem>
                    <SelectItem value="50">Within 50 miles</SelectItem>
                    <SelectItem value="nationwide">Nationwide</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button type="submit" className="h-11 bg-violet-600 hover:bg-violet-500" data-testid="clubs-search-btn">
                  Search
                </Button>
              </form>
              
              <Button
                variant="outline"
                className="h-11 border-slate-200"
                onClick={() => setShowFilters(!showFilters)}
                data-testid="clubs-filter-btn"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 pt-4 border-t border-slate-200"
            >
              <div className="flex flex-wrap gap-4 items-end">
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Location</label>
                  <Select value={selectedLocation} onValueChange={(val) => setSelectedLocation(val === "all" ? "" : val)}>
                    <SelectTrigger data-testid="location-filter">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {ukRegions.map(region => (
                        <SelectItem key={region} value={region}>{region}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-48">
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Skill Level</label>
                  <Select value={selectedSkill} onValueChange={(val) => setSelectedSkill(val === "all" ? "" : val)}>
                    <SelectTrigger data-testid="skill-filter">
                      <SelectValue placeholder="All Levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      {skillLevels.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button onClick={applyFilters} className="bg-violet-600 hover:bg-violet-500" data-testid="apply-filters-btn">
                  Apply Filters
                </Button>
                <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Club Grid */}
      <div className="container mx-auto px-6 lg:px-12 py-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <div className="h-48 bg-slate-200 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-6 bg-slate-200 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                  <div className="h-16 bg-slate-200 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : clubs.length === 0 ? (
          <EmptyState
            icon={Music}
            title="No clubs found"
            description="Try adjusting your search or filters"
            action={
              <Button onClick={clearFilters} variant="outline" data-testid="clear-search-btn">
                Clear Search
              </Button>
            }
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clubs.map((club, index) => (
              <motion.div
                key={club.club_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link to={`/clubs/${club.slug || club.club_id}`} data-testid={`club-item-${club.club_id}`}>
                  <div className="group relative overflow-hidden rounded-2xl bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={club.image_url || 'https://static.prod-images.emergentagent.com/jobs/f6783fbe-a9ce-415b-8816-9076f9ce2a87/images/ff49b4127a0591960b40099bf65a8c4810d0a523c4be0dd2d50acb65d4a3b422.png'}
                        alt={club.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {club.is_featured && (
                        <div className="absolute top-3 left-3">
                          <Badge className="bg-violet-500 text-white hover:bg-violet-500">Featured</Badge>
                        </div>
                      )}
                      <button 
                        className="absolute top-3 right-3 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white transition-colors"
                        onClick={(e) => e.preventDefault()}
                      >
                        <Heart className="w-5 h-5 text-slate-600 hover:text-violet-500" />
                      </button>
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-bold text-slate-900 group-hover:text-violet-600 transition-colors">
                          {club.name}
                        </h3>
                        <div className="flex items-center gap-0.5">
                          {renderStars(club.rating)}
                          <span className="text-xs text-slate-400 ml-1">({club.review_count})</span>
                        </div>
                      </div>
                      <p className="flex items-center text-slate-400 text-sm mb-1">
                        <MapPin className="w-4 h-4 mr-1" />
                        {club.location || [club.city, club.region, club.country].filter(Boolean).join(', ') || 'Location not specified'}
                      </p>
                      {club.additional_locations && club.additional_locations.length > 0 && (
                        <p className="text-xs text-slate-400 mb-3 pl-5">
                          + {club.additional_locations.map(loc => loc.address).join(', ')}
                        </p>
                      )}
                      {(!club.additional_locations || club.additional_locations.length === 0) && <div className="mb-3" />}
                      <p className="text-slate-600 text-sm line-clamp-2 mb-4">{club.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {club.skill_levels?.slice(0, 3).map((level, i) => (
                          <Badge 
                            key={i} 
                            variant="secondary"
                            className={`text-xs ${
                              level === 'Beginner' ? 'bg-teal-100 text-teal-700' :
                              level === 'Intermediate' ? 'bg-violet-100 text-violet-700' :
                              'bg-slate-800 text-white'
                            }`}
                          >
                            {level}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
